import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const money = (sym, v) => `${sym || "€"}${Number(v || 0).toFixed(2)}`;
const SYMBOL = { eur: "€", gbp: "£", usd: "$" };
const MAX_TRIES = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0, active = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/payments/status/${sessionId}`);
        if (!active) return;
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (!active) return;
      if (++tries >= MAX_TRIES) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const sym = SYMBOL[info?.currency] || "€";

  return (
    <div className="min-h-screen bg-navy grain-navy flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-blue-600" size={40} />
            <h1 className="mt-4 text-xl font-bold text-navy">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-500">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={48} data-testid="payment-success-icon" />
            <h1 className="mt-4 text-2xl font-bold text-navy">Payment received</h1>
            <p className="mt-2 text-sm text-slate-600">
              Thank you! Your payment {info?.amount ? `of ${money(sym, info.amount)} ` : ""}
              {info?.invoice_number ? `for invoice ${info.invoice_number} ` : ""}was successful.
            </p>
            <p className="mt-1 text-xs text-slate-400">A receipt has been recorded against your invoice.</p>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto text-amber-500" size={40} />
            <h1 className="mt-4 text-xl font-bold text-navy">Still processing</h1>
            <p className="mt-2 text-sm text-slate-600">Your payment is being confirmed. It's safe to close this page — your invoice will update shortly.</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto text-red-500" size={40} />
            <h1 className="mt-4 text-xl font-bold text-navy">We couldn't confirm this payment</h1>
            <p className="mt-2 text-sm text-slate-600">If you were charged, don't worry — please contact the business and they can check.</p>
          </>
        )}
        <Link to="/" className="mt-6 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700" data-testid="payment-home-link">Back to Amden One</Link>
      </div>
    </div>
  );
}
